import { message, notification } from 'antd'
import type { ArgsProps } from 'antd/es/notification/interface'
import React, {
    CSSProperties,
    FC,
    createContext,
    useContext,
    useEffect,
    useMemo,
} from 'react'
import { tryGetShortcutKey } from '../utils/shortcutKeyUtils'

/** The interface for the NotificationContext */
export interface NotificationContextProps {
    /** The success notification method */
    success: (config: ArgsProps) => void
    /** The error notification method */
    error: (config: ArgsProps) => void
    /** The info notification method */
    info: (config: ArgsProps) => void
    /** The warning notification method */
    warning: (config: ArgsProps) => void
    /** The loading notification method */
    loading: (config: ArgsProps) => void
    /** The message notification API */
    messageApi: typeof message
    /** The notification API */
    notificationApi: typeof notification
}

/** The context for use with the NotificationProvider */
const NotificationContext = createContext<NotificationContextProps | undefined>(
    undefined
)

const DEFAULT_CONFIG: Partial<ArgsProps> = {
    duration: 3,
    placement: 'top',
}

/** The notification types */
type NotificationType = 'success' | 'error' | 'info' | 'warning' | 'loading'
/** The default styles for each notification type */
const STYLES: Partial<Record<NotificationType, CSSProperties>> = {
    success: { backgroundColor: 'hsla(128, 71%, 50%, 0.12)' },
    error: { backgroundColor: 'hsla(0, 71%, 50%, 0.12)' },
    info: { backgroundColor: 'hsla(191, 71%, 50%, 0.12)' },
    warning: { backgroundColor: 'hsla(55, 71%, 50%, 0.12)' },
}

let activeNotificationKeys: string[] = []

/**
 * The notification provider
 * @param children The child components
 * @returns The notification provider */
export const NotificationProvider: FC = ({ children }) => {
    const [api, contextHolder] = notification.useNotification()

    /** The notification context */
    const notifications = useMemo(() => {
        const key = 'updatable'

        const createHandler = (type: NotificationType) => (
            override: ArgsProps
        ) => {
            message.config({ top: 100, duration: type === 'loading' ? 0 : 3 })
            if (!override.description) {
                return message.open({
                    key,
                    type,
                    content: override.message,
                })
            }

            if (type === 'loading') return

            const notificationKey =
                override.key?.toString() || `${type}-${Date.now()}`
            activeNotificationKeys.push(notificationKey)

            api[type]({
                ...DEFAULT_CONFIG,
                style: STYLES[type],
                ...override,
                key: notificationKey,
                onClose: () => {
                    // remove from tracking when closed
                    activeNotificationKeys = activeNotificationKeys.filter(
                        (k) => k !== notificationKey
                    )
                    override.onClose?.()
                },
            })
        }

        return {
            success: createHandler('success'),
            error: createHandler('error'),
            info: createHandler('info'),
            warning: createHandler('warning'),
            loading: createHandler('loading'),
            messageApi: message,
            notificationApi: notification,
        }
    }, [api])

    // register an event that closes notification (by key) when 'Esc' is pressed
    useEffect(() => {
        const abortController = new AbortController()

        const onKey = (event: KeyboardEvent) => {
            if (tryGetShortcutKey('Escape', event)) {
                if (activeNotificationKeys.length > 0) {
                    // pop and close the last notification
                    const keyToClose = activeNotificationKeys.pop()
                    if (keyToClose) {
                        api.destroy(keyToClose)
                    }
                } else {
                    api.destroy()
                }
            }
        }

        // register handler when'Esc' is pressed
        document.addEventListener('keydown', onKey, {
            signal: abortController.signal,
        })

        return () => abortController.abort()
    }, [api])

    return (
        <NotificationContext.Provider value={notifications}>
            {contextHolder}
            {children}
        </NotificationContext.Provider>
    )
}

/**
 * The notification hook
 * @returns The notification context
 */
export const useNotification = (): NotificationContextProps => {
    const context = useContext(NotificationContext)

    if (!context) {
        throw new Error(
            'useNotificationContext must be used within NotificationProvider'
        )
    }
    return context
}
